/** @jsxImportSource @emotion/react */
import * as R from 'ramda';
import * as React from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import sadMagnifyingGlass from '../assets/icons/magnifyingGlassSad.svg';
import { Loader } from '../forms/elements/loader/Loader';
import { GuestBasics } from '../guest/guestBasics';
import GuestSnackbarDispatcher from '../guest/GuestSnackbarDispatcher';
import { isNilOrEmpty, sortBy, sortByProp, assoc } from '../language/module';
import { calcAge } from '../language/time';
import { FADE_IN_ANIMATION } from '../styles/animations';
import { RED_CREEK } from '../styles/colors';
import { LargeText, MediumText } from '../styles/styles';
import { ExpandingGuestSearchResult } from './ExpandingGuestSearchResultFunc';
import {SortByMenu} from './SortByMenu';
import ImmutableMap from '../language/ImmutableMap';
import { EntitlementType } from '../entitlementTypes/entitlementType';
import { Resort } from '../resortDetails/resort';
import { getResource } from '../language/resources/getResource';


const ROOT_STYLE = css`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  flex-shrink: 0;
  ${MediumText};
  padding-top: 20px;
`;

const ResultText = styled('span')`
  ${MediumText};
  margin-bottom: 20px;
`;

const ErrorText = styled('span')`
  ${MediumText};
  color: ${RED_CREEK};
`;

const QueryText = styled('span')`
  ${LargeText};
  font-weight: bold;
`;

const AngrySearch = (
  props,
) => <div
  css={css`
    ${FADE_IN_ANIMATION};
    display:         flex;
    flex-direction:  column;
    align-items:     center;
    justify-content: center;
    height:          100%;
    margin:          auto;
  `}
>
  <img
    alt='No Search Results'
    src={sadMagnifyingGlass}
    css={css`width: 250px; height: 250px; margin-bottom: 10px;`}
  />
  {props.children}
</div>;

type RouterHistoryFacade = {
  replace: (to: string) => void;
  push: (to: string) => void;
};

type Props = {
  findGuest: (query: string) => void;
  fetchEntitlementTypes: () => void;
  isLoading?: boolean;
  resultsQuery?: string;
  results?: GuestBasics[];
  error?: string;
  isLoadingEntitlementTypes?: boolean;
  allEntitlementTypes?: ImmutableMap<EntitlementType>;
  selectedResort: Resort;
  resources: Record<string, any>;

  // Router props
  location: { pathname: string };
  history: RouterHistoryFacade;
  params?: Record<string, string | undefined>;

  // Provided by wrapper
  sortBy?: string;
  sortAsc?: boolean;
  setSort?: (sortProp?: string, sortAsc?: boolean) => void;

  rawSearch?: string;
};

type State = {
  sortedResults: GuestBasics[];
  search: string;
};

/**
 * GuestSearchResults: a component that renders a list of households for guests matching a particular guest search. This
 * component is connected to redux so that it can initiate the HTTP request for its input search which it gets from
 * the URL.
 *
 * PureComponent: true
 * Connected to Redux: true
 */
export class GuestSearchResults extends React.PureComponent<Props, State> {
  public state: State = {
    sortedResults: [],
    search: "",
  };

  private getQuery = (pathName: string): string => R.pipe(
    R.split('/'),
    R.last,
    decodeURIComponent,
  )(pathName || '');


  private sortResults = (prop: string, dir: boolean, data: GuestBasics[]) => prop === 'dob'
    ? sortBy((guest: GuestBasics) => calcAge(guest.dob), dir, data)
    : sortByProp(prop, true, dir, data);


  private setCurrentSortedResults = (
    results: GuestBasics[] = [],
    sortByThis?: string | null,
    sortDir: boolean       = false,
  ) => this.setState(prev => assoc(
    'sortedResults',
    !isNilOrEmpty(sortByThis) ? this.sortResults(sortByThis!, sortDir, results) : results,
    prev,
  ));


  public componentDidMount() {
    const query = this.getQuery(this.props.location.pathname);
    
    this.setState((prev) => assoc("search", this.props.rawSearch ?? "", prev));

    if (
      !this.props.isLoading
      && !this.props.error
      && (!this.props.results || this.props.resultsQuery !== query)
    ) {
      this.props.findGuest(query);
    } else {
      this.setCurrentSortedResults(this.props.results);
    }
  }


  public componentDidUpdate(prevProps) {
    // Fetch entitlement types if not available
    if (!this.props.isLoadingEntitlementTypes && !this.props.allEntitlementTypes) {
      this.props.fetchEntitlementTypes!();
    }

    if (
      prevProps.location.pathname !== this.props.location.pathname ||
      prevProps.selectedResort !== this.props.selectedResort) {
      this.props.findGuest(this.getQuery(this.props.location.pathname));
    }

    // Re-sort when results or search params change
    const presortResults = this.props.results ?? [];
    if (
      prevProps.results !== this.props.results ||
      prevProps.sortBy !== this.props.sortBy ||
      prevProps.sortAsc !== this.props.sortAsc ||
      prevProps.rawSearch !== this.props.rawSearch
    ) {
      this.setCurrentSortedResults(presortResults, this.props.sortBy, !!this.props.sortAsc);
      this.setState((prev) => assoc("search", this.props.rawSearch ?? "", prev));
    }
  }

  private onSortChange = (sortProp: string, sortAsc: boolean) => {
    if (isNilOrEmpty(sortProp)) {
      // Clear sorting params (e.g., "Relevance" dropdown)
      this.props.setSort?.(undefined, undefined);
      return;
    }
    this.props.setSort?.(sortProp, sortAsc);
  };

  public render = () => {
    if (!isNilOrEmpty(this.props.error)) {
      return <AngrySearch><ErrorText>{this.props.error}</ErrorText></AngrySearch>;
    }

    if (this.props.isLoading || this.props.isLoadingEntitlementTypes) {
      return <Loader rootCss={css`margin-top: auto; margin-bottom: auto;`} />;
    }

    const results     = this.state.sortedResults as GuestBasics[];
    const query = this.getQuery(this.props.location.pathname);

    const defaultSort =
      this.props.sortBy != null
        ? `${this.props.sortBy}${this.props.sortAsc ? "Asc" : "Desc"}`
        : undefined;

    return (
      <div css={ROOT_STYLE}>
        {/* Search Result Summary e.g. '25 of 1500 matches' */
          results.length > 0 && (
          <div css={css`display: flex; justify-content: space-between;`}>
           <ResultText>
              {results.length === 1
                ? getResource(this.props.resources, "GUEST_SEARCH_TOP_ACCOUNT_MATCH")
                : getResource(
                    this.props.resources,
                    "GUEST_SEARCH_TOP_ACCOUNTS_MATCH",
                    results.length
                  )}
              <QueryText>{query}</QueryText>
            </ResultText>
          <div>
            {getResource(this.props.resources, 'GUEST_SEARCH_SORT_BY')}
            <SortByMenu defaultSort={defaultSort} onSortChange={this.onSortChange} />
            </div>
          </div>
        )}
        {/* Search Results */}
        {results.map((guest, index) => (
          <ExpandingGuestSearchResult 
            key={guest.masterGuestId} 
            guest={guest} 
            entitlementTypes={this.props.allEntitlementTypes} 
            isLoadingEntitlementTypes={!!this.props.isLoadingEntitlementTypes}
            resultCount={this.props.results ? this.props.results.length : 0} 
            rootCss={css`margin-bottom: 40px; animation-duration: ${0.5 * index}s;`}/>
        ))}
        {/* No Search Results */
          results.length === 0 && (
            <AngrySearch>
              <ResultText>
                {getResource(this.props.resources, 'GUEST_SEARCH_RESULTS_NO_ACCOUNTS_MATCH')}
                <QueryText>{this.getQuery(this.props.location.pathname)}</QueryText>
                {getResource(this.props.resources, 'GUEST_SEARCH_RESULTS_PLEASE_SEARCH_AGAIN')}
              </ResultText>
            </AngrySearch>
            )
        }
        {/* Listens for guest events (network failures etc) and shows snackbars accordingly */}
        <GuestSnackbarDispatcher />
      </div>
    );
  };
};
